/**
 * Fragment de routes du paquet v5-p02-missions-code-ecrans-1 (vague V5), rendu par le registre v10. Composants chargés
 * à la demande (règle de chargement du registre). Le projet est passé par `?projet=`, sinon celui de la coquille.
 * Routes du socle remplacées (retrait de l'historique, D2 ; YAML `couvre: route:…`) :
 *  - `/missions` → projet-missions (MissionsScreen ; les vues `?mission=` et `?vue=lancer` restent servies par
 *    l'écran historique jusqu'au paquet v4-p04 qui réalise la fiche et le lancement) ;
 *  - `/history` → projet-journal-traitements (HistoryHub) ;
 *  - `/publication` → projet-publication-code (PublicationScreen) ;
 *  - `/tests/cas/:caseId` → projet-cas-de-test-fiche (CasFiche ; l'édition du cas, `?vue=edition` et l'ancre
 *    `#scenario`, reste servie par l'écran historique jusqu'au paquet v6-p01 qui réalise l'éditeur).
 * Routes neuves : les fiches et les dialogues du paquet (chacun sa route).
 */
import { lazy, Suspense, type ComponentType, type JSX } from 'react'

import type { Session } from '../../auth/session'
import type { ContexteRoutes, FragmentRoutes } from './registre'

const ProjetMissions = lazy(() => import('../ecrans/v5/missions-1/ProjetMissions'))
const ProjetJournalTraitements = lazy(() => import('../ecrans/v5/missions-1/ProjetJournalTraitements'))
const ProjetPublicationCode = lazy(() => import('../ecrans/v5/missions-1/ProjetPublicationCode'))
const DialoguePublierCode = lazy(() => import('../ecrans/v5/missions-1/DialoguePublierCode'))
const ProjetPublicationFiche = lazy(() => import('../ecrans/v5/missions-1/ProjetPublicationFiche'))
const DialogueDeclencherPipeline = lazy(() => import('../ecrans/v5/missions-1/DialogueDeclencherPipeline'))
const ProjetPipelineDepotFiche = lazy(() => import('../ecrans/v5/missions-1/ProjetPipelineDepotFiche'))
const DialogueEcarterUneVulnerabilite = lazy(() => import('../ecrans/v5/missions-1/DialogueEcarterUneVulnerabilite'))
const ProjetFusionFiche = lazy(() => import('../ecrans/v5/missions-1/ProjetFusionFiche'))
const ProjetCasDeTestFiche = lazy(() => import('../ecrans/v5/missions-1/ProjetCasDeTestFiche'))
const DialogueValiderTest = lazy(() => import('../ecrans/v5/missions-1/DialogueValiderTest'))
const ProjetExecutionCommandesFiche = lazy(() => import('../ecrans/v5/missions-1/ProjetExecutionCommandesFiche'))
const ProjetPartageApercuFiche = lazy(() => import('../ecrans/v5/missions-1/ProjetPartageApercuFiche'))
const ProjetRetourGenerationEditeur = lazy(() => import('../ecrans/v5/missions-1/ProjetRetourGenerationEditeur'))
const ProjetRetourGenerationFiche = lazy(() => import('../ecrans/v5/missions-1/ProjetRetourGenerationFiche'))

function ecran(Composant: ComponentType<{ session: Session }>) {
  return ({ session, t }: ContexteRoutes): JSX.Element => (
    <Suspense fallback={<p role="status">{t('v10.primitives.assemblage.chargement')}</p>}>
      <Composant session={session} />
    </Suspense>
  )
}

export const fragment: FragmentRoutes = {
  zone: 'v5-missions-1',
  routes: [
    { chemin: '/missions', element: ecran(ProjetMissions), remplace: true },
    { chemin: '/history', element: ecran(ProjetJournalTraitements), remplace: true },
    { chemin: '/publication', element: ecran(ProjetPublicationCode), remplace: true },
    { chemin: '/publication/publier', element: ecran(DialoguePublierCode) },
    { chemin: '/publication/applications/:slug/revisions/:revision', element: ecran(ProjetPublicationFiche) },
    { chemin: '/publication/pipelines/declencher', element: ecran(DialogueDeclencherPipeline) },
    { chemin: '/publication/pipelines/:pipelineId', element: ecran(ProjetPipelineDepotFiche) },
    { chemin: '/publication/pipelines/:pipelineId/ecarter', element: ecran(DialogueEcarterUneVulnerabilite) },
    { chemin: '/publication/fusions/nouvelle', element: ecran(ProjetFusionFiche) },
    { chemin: '/publication/fusions/:iid', element: ecran(ProjetFusionFiche) },
    { chemin: '/tests/cas/:caseId', element: ecran(ProjetCasDeTestFiche), remplace: true },
    { chemin: '/tests/cas/:caseId/valider', element: ecran(DialogueValiderTest) },
    { chemin: '/pipelines/:suiteId/executions/:runId', element: ecran(ProjetExecutionCommandesFiche) },
    { chemin: '/apercu/:apercuId/partages/:shareId', element: ecran(ProjetPartageApercuFiche) },
    { chemin: '/feedback/nouveau', element: ecran(ProjetRetourGenerationEditeur) },
    { chemin: '/feedback/:retourId', element: ecran(ProjetRetourGenerationFiche) },
  ],
}
